import React, { useCallback, useEffect, useRef } from 'react';
import { Participant } from 'livekit-client';
import { observer } from 'mobx-react-lite';
import { type ParticipantLikeObject } from 'features/Room/hooks/useInitialSort';
import { useSort, fixedSlotsCount } from 'features/Room/hooks/useSort';
import { selectParticipantsSortOrder } from 'state/selectors';
import { participants } from './test';
import { AddParticipantButton, Buttons, PageContainer, TestTable, TestTableTr } from './view';

const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date
        .getSeconds()
        .toString()
        .padStart(2, '0')}`;
};

export const TestSortPage: React.FC = observer(() => {
    const { setSortedParticipants, findAndSwapOldestSpeakers, findAndSwapOldestSpeakerWithScreenSharer } = useSort();
    const sortOrder = selectParticipantsSortOrder();
    const participantsMap = new Map(participants.map((item) => [item.identity, item]));
    const sortedParticipants = sortOrder.map((id) => participantsMap.get(id)) as ParticipantLikeObject[];

    const prevParticipantState = useRef<ParticipantLikeObject[]>(null);

    useEffect(() => {
        setSortedParticipants(participants);
    }, []);

    useEffect(() => {
        prevParticipantState.current = [...sortedParticipants];
    }, [sortedParticipants]);

    const renderTestParticipants = (participants: ParticipantLikeObject[]) =>
        participants.map((participant, i) => {
            const { joinedAt, lastSpokeAt } = participant;
            const joinedAtString = joinedAt ? formatDate(joinedAt) : '';
            const lastSpokeAtString = formatDate(lastSpokeAt);

            const maybePrevIndex = prevParticipantState.current?.findIndex(
                ({ identity }) => identity === participant.identity,
            );
            const prevPosition = maybePrevIndex === undefined || maybePrevIndex === -1 ? i : maybePrevIndex;

            return (
                <TestTableTr
                    key={participant.identity}
                    $highlighted={i < fixedSlotsCount}
                    $positionChanged={prevPosition !== i}
                >
                    <td>{i}</td>
                    <td align="center">{participant.identity}</td>
                    <td align="center">{participant.isLocal ? 'true' : 'false'}</td>
                    <td align="center">{participant.isScreenShareEnabled ? 'true' : 'false'}</td>
                    <td>{joinedAtString}</td>
                    <td>{lastSpokeAtString}</td>
                    <td>{prevPosition}</td>
                </TestTableTr>
            );
        });

    const handleAddSpeaker = useCallback(() => {
        const newSpeakersCount = 2;
        const speakingParticipants = Array(newSpeakersCount)
            .fill(0)
            .map(() => {
                const i = Math.floor(Math.random() * sortedParticipants.length);
                sortedParticipants[i].lastSpokeAt = new Date(Date.now() + 1000 * 60 * 60 + 17);
                return sortedParticipants[i];
            });

        findAndSwapOldestSpeakers<ParticipantLikeObject>(speakingParticipants, sortedParticipants as Participant[]);
    }, [sortedParticipants]);

    const handleAddScreenSharer = useCallback(() => {
        // Отключаем шаринг у других участников
        sortedParticipants.forEach((participant) => {
            if (participant.isScreenShareEnabled) {
                participant.isScreenShareEnabled = false;
            }
        });

        // Включаем шаринг у произвольного пользователя
        const i = Math.floor(Math.random() * sortedParticipants.length);
        sortedParticipants[i].isScreenShareEnabled = true;
        const screenSharerParticipant = sortedParticipants[i];

        findAndSwapOldestSpeakerWithScreenSharer<ParticipantLikeObject>(
            screenSharerParticipant,
            sortedParticipants as Participant[],
        );
    }, [sortedParticipants]);

    return (
        <PageContainer>
            <Buttons>
                <AddParticipantButton onClick={handleAddSpeaker}>Добавить нового спикера</AddParticipantButton>
                <AddParticipantButton onClick={handleAddScreenSharer}>Пошарить экран</AddParticipantButton>
            </Buttons>
            <TestTable>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>identity</th>
                        <th>isLocal</th>
                        <th>isScreenSharedEnabled</th>
                        <th>joinedAt</th>
                        <th>lastSpokeAt</th>
                        <th>previousPosition</th>
                    </tr>
                </thead>
                <tbody>{renderTestParticipants(sortedParticipants)}</tbody>
            </TestTable>
        </PageContainer>
    );
});
